import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getCallSuggestions, updateSuggestion, updateCallNotes } from '../api';
import { useI18n } from '../context/I18nContext';
import { useToast } from '../context/ToastContext';
import { ArrowLeft, Play, Pause, Phone, Clock, Flame, Calendar, User, MessageSquare, CheckCircle, XCircle, Sparkles, Search, X, Copy, Check, StickyNote, Save, Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import { formatDuration } from '../utils';

// Highlight matching parts of text without mangling whitespace.
function highlight(text, query) {
  if (!query || !text) return text;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${safe})`, 'gi'));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-accent-yellow/40 text-white rounded px-0.5">{p}</mark>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

function AudioPlayer({ url: initialUrl, callId }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [url, setUrl] = useState(initialUrl || '');
  const [resolveError, setResolveError] = useState('');

  // Always prefer the bulletproof playback URL (Supabase mirror) when callId
  // is provided. Falls back to the original Retell URL on error.
  useEffect(() => {
    let cancelled = false;
    if (!callId) return;
    (async () => {
      try {
        const { data } = await api.get(`/api/calls/${callId}/playback-url`);
        if (!cancelled && data?.url) setUrl(data.url);
      } catch (e) {
        // Keep the initial URL; only surface error if neither URL works
        if (!initialUrl) setResolveError('Opptak ikke tilgjengelig');
      }
    })();
    return () => { cancelled = true; };
  }, [callId, initialUrl]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
    setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100 || 0);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (!url && resolveError) {
    return (
      <div className="text-sm text-slate-500 italic">{resolveError}</div>
    );
  }

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-4" data-testid="audio-player">
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => setPlaying(false)}
      />
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          data-testid="audio-play-button"
          className="w-10 h-10 rounded-full bg-accent-blue hover:bg-accent-blue-hover flex items-center justify-center transition-colors"
        >
          {playing ? <Pause size={16} className="text-white" /> : <Play size={16} className="text-white ml-0.5" />}
        </button>
        <div className="flex-1">
          <div
            className="h-1.5 bg-navy-700 rounded-full cursor-pointer relative overflow-hidden"
            onClick={handleSeek}
          >
            <div
              className="absolute left-0 top-0 h-full bg-accent-blue rounded-full transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-xs text-slate-500 font-mono">{fmt(currentTime)}</span>
            <span className="text-xs text-slate-500 font-mono">{fmt(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function QualityRatingCard({ call, onRated }) {
  const toast = useToast();
  const [rating, setRating] = useState(call.quality_rating || null);
  const [comment, setComment] = useState(call.quality_rating_comment || '');
  const [showComment, setShowComment] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (newRating) => {
    setSaving(true);
    try {
      const id = call.call_id || call._id;
      const { data } = await api.post(`/api/calls/${id}/rating`, { rating: newRating, comment });
      setRating(data.rating);
      onRated && onRated(data);
      toast.success(newRating === 'good' ? 'Takk for tilbakemeldingen!' : 'Takk — vi tar dette med oss');
      if (newRating === 'bad') setShowComment(true);
      else setShowComment(false);
    } catch (e) {
      console.error(e);
      toast.error('Kunne ikke lagre vurdering');
    } finally {
      setSaving(false);
    }
  };

  const saveComment = async () => {
    if (!rating) return;
    setSaving(true);
    try {
      const id = call.call_id || call._id;
      const { data } = await api.post(`/api/calls/${id}/rating`, { rating, comment });
      onRated && onRated(data);
      toast.success('Kommentar lagret');
      setShowComment(false);
    } catch (e) {
      toast.error('Kunne ikke lagre kommentar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-5 mb-6" data-testid="call-quality-rating">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">Hvordan håndterte AI-en denne samtalen?</h3>
          <p className="text-xs text-slate-500">Tilbakemeldingen din hjelper oss å forbedre agenten.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => submit(rating === 'good' ? null : 'good')}
            disabled={saving}
            data-testid="rating-thumbs-up"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors ${
              rating === 'good'
                ? 'border-accent-green bg-accent-green/15 text-accent-green'
                : 'border-navy-600 text-slate-400 hover:border-accent-green/50 hover:text-accent-green'
            }`}
            title="Bra"
          >
            <ThumbsUp size={14} fill={rating === 'good' ? 'currentColor' : 'none'} />
            <span className="text-xs font-medium">Bra</span>
          </button>
          <button
            onClick={() => submit(rating === 'bad' ? null : 'bad')}
            disabled={saving}
            data-testid="rating-thumbs-down"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors ${
              rating === 'bad'
                ? 'border-accent-red bg-accent-red/15 text-accent-red'
                : 'border-navy-600 text-slate-400 hover:border-accent-red/50 hover:text-accent-red'
            }`}
            title="Dårlig"
          >
            <ThumbsDown size={14} fill={rating === 'bad' ? 'currentColor' : 'none'} />
            <span className="text-xs font-medium">Dårlig</span>
          </button>
        </div>
      </div>
      {(showComment || (rating === 'bad' && !call.quality_rating_comment)) && rating === 'bad' && (
        <div className="mt-4">
          <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Hva gikk galt? (valgfritt)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="F.eks. AI-en forsto ikke spørsmålet, ga feil informasjon, eller var for sen å svare..."
            className="w-full bg-navy-700 border border-navy-600 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue resize-y"
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button onClick={() => setShowComment(false)} disabled={saving} className="text-xs text-slate-400 hover:text-white">Avbryt</button>
            <button onClick={saveComment} disabled={saving} className="text-xs px-3 py-1.5 bg-accent-blue hover:bg-accent-blue-hover text-white rounded-md transition-colors disabled:opacity-50">
              {saving ? 'Lagrer...' : 'Lagre kommentar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NotesCard({ call }) {
  const [notes, setNotes] = useState(call.notes || '');
  const [original, setOriginal] = useState(call.notes || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(call.notes_updated_at || null);
  const [error, setError] = useState('');

  const dirty = notes !== original;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const id = call.call_id || call._id;
      const { data } = await updateCallNotes(id, notes);
      setOriginal(notes);
      setSavedAt(data.notes_updated_at || new Date().toISOString());
    } catch (e) {
      console.error(e);
      setError('Kunne ikke lagre notat');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6" data-testid="call-notes-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StickyNote size={14} className="text-accent-yellow" />
          <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500">Mine notater</h3>
        </div>
        {savedAt && !dirty && (
          <span className="text-xs text-slate-500">
            Lagret {new Date(savedAt).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Legg til notater om denne samtalen — oppfølginger, viktige detaljer, neste steg..."
        rows={4}
        maxLength={5000}
        className="w-full bg-navy-700 border border-navy-600 rounded-md px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue resize-y leading-relaxed"
        data-testid="call-notes-textarea"
      />
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-slate-500">{notes.length}/5000</span>
        <div className="flex items-center gap-3">
          {error && <span className="text-xs text-accent-red">{error}</span>}
          {dirty && (
            <button
              onClick={() => { setNotes(original); setError(''); }}
              disabled={saving}
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              Forkast
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-blue hover:bg-blue-600 text-white text-xs font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="call-notes-save"
          >
            <Save size={12} />
            {saving ? 'Lagrer...' : 'Lagre notat'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TranscriptCard({ call }) {
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const toast = useToast();
  const messages = useMemo(() => {
    if (Array.isArray(call.transcript_object) && call.transcript_object.length > 0) {
      return call.transcript_object;
    }
    return null;
  }, [call.transcript_object]);

  const filtered = useMemo(() => {
    if (!messages || !query) return messages;
    const q = query.toLowerCase();
    return messages.filter(m => (m.content || '').toLowerCase().includes(q));
  }, [messages, query]);

  const matchCount = useMemo(() => {
    if (!query) return 0;
    if (messages) {
      return messages.reduce((acc, m) => {
        const c = (m.content || '').toLowerCase();
        const q = query.toLowerCase();
        let i = 0, n = 0;
        while ((i = c.indexOf(q, i)) !== -1) { n++; i += q.length; }
        return acc + n;
      }, 0);
    }
    if (call.transcript) {
      const c = String(call.transcript).toLowerCase();
      const q = query.toLowerCase();
      let i = 0, n = 0;
      while ((i = c.indexOf(q, i)) !== -1) { n++; i += q.length; }
      return n;
    }
    return 0;
  }, [query, messages, call.transcript]);

  const handleCopy = async () => {
    try {
      const text = messages
        ? messages.map(m => {
            const startSec = (Array.isArray(m.words) && m.words.length > 0 && typeof m.words[0].start === 'number')
              ? m.words[0].start
              : (typeof m.start === 'number' ? m.start : null);
            const ts = startSec !== null ? `[${Math.floor(startSec / 60)}:${String(Math.floor(startSec % 60)).padStart(2, '0')}] ` : '';
            return `${ts}${m.role === 'agent' ? 'AI' : 'Innringer'}: ${m.content}`;
          }).join('\n')
        : (call.transcript || '');
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('clipboard failed', e);
      toast.error('Kunne ikke kopiere til utklippstavlen');
    }
  };

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500">Transkripsjon</h3>
        <div className="flex items-center gap-2">
          {(messages || call.transcript) && (
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Søk i transkripsjon..."
                className="pl-8 pr-7 py-1.5 bg-navy-700 border border-navy-600 rounded-md text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-accent-blue w-56"
                data-testid="transcript-search"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X size={12} />
                </button>
              )}
            </div>
          )}
          {query && (
            <span className="text-xs text-slate-500 font-mono">{matchCount} treff</span>
          )}
          {(messages || call.transcript) && (
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-400 hover:text-white border border-navy-600 rounded-md hover:border-accent-blue transition-colors"
              title="Kopier transkripsjon"
              data-testid="transcript-copy"
            >
              {copied ? <><Check size={12} className="text-accent-green" />Kopiert</> : <><Copy size={12} />Kopier</>}
            </button>
          )}
        </div>
      </div>
      <div className="space-y-4" data-testid="call-transcript">
        {filtered && filtered.length > 0 ? (
          filtered.map((msg, i) => {
            // Pull start time from words[0].start, or msg.start, or null
            const startSec = (Array.isArray(msg.words) && msg.words.length > 0 && typeof msg.words[0].start === 'number')
              ? msg.words[0].start
              : (typeof msg.start === 'number' ? msg.start : null);
            const ts = startSec !== null
              ? `${Math.floor(startSec / 60)}:${String(Math.floor(startSec % 60)).padStart(2, '0')}`
              : null;
            return (
              <div key={i} className={`flex ${msg.role === 'agent' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-lg ${
                  msg.role === 'agent'
                    ? 'bg-navy-700 text-slate-300'
                    : 'bg-accent-blue/15 text-slate-200'
                }`}>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className={`text-xs font-semibold uppercase tracking-wider ${msg.role === 'agent' ? 'text-accent-blue' : 'text-accent-green'}`}>
                      {msg.role === 'agent' ? '🤖 AI Resepsjonist' : '👤 Innringer'}
                    </div>
                    {ts && (
                      <span className="text-[10px] font-mono text-slate-500 tabular-nums" title={`Tidspunkt i samtalen: ${ts}`}>{ts}</span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed">{highlight(msg.content, query)}</p>
                </div>
              </div>
            );
          })
        ) : filtered && filtered.length === 0 ? (
          <p className="text-sm text-slate-500">Ingen treff for “{query}”</p>
        ) : call.transcript ? (
          <pre className="text-sm text-slate-300 whitespace-pre-wrap font-body leading-relaxed">{highlight(call.transcript, query)}</pre>
        ) : (
          <p className="text-sm text-slate-500">Ingen transkripsjon tilgjengelig</p>
        )}
      </div>
    </div>
  );
}

function MetaBadge({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={color || 'text-slate-500'} />
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">{label}</span>
      </div>
      <div className="text-sm font-medium text-white">{value}</div>
    </div>
  );
}

export default function CallDetail() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const toast = useToast();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get(`/api/calls/${callId}`);
        setCall(data);
      } catch (err) {
        console.error(err);
        setError(err.response?.status === 404 ? 'Samtalen ble ikke funnet' : 'Kunne ikke laste samtaledetaljer');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [callId]);

  useEffect(() => {
    if (!callId) return;
    getCallSuggestions(callId).then(res => {
      // Backend returns { suggestions: [...] } — handle both shapes defensively
      const data = res?.data;
      const list = Array.isArray(data) ? data : (Array.isArray(data?.suggestions) ? data.suggestions : []);
      setSuggestions(list);
    }).catch(() => setSuggestions([]));
  }, [callId]);

  const handleSuggestionAction = async (suggestionId, status) => {
    try {
      await updateSuggestion(suggestionId, status);
      setSuggestions(prev => prev.filter(s => s._id !== suggestionId));
      toast.success(status === 'done' ? 'Oppgave markert som utført' : 'Forslag avvist');
    } catch (err) {
      console.error(err);
      toast.error('Kunne ikke oppdatere forslag');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-navy-800 rounded w-48" />
        <div className="h-40 bg-navy-800 rounded-lg" />
      </div>
    );
  }

  if (!call) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="max-w-sm text-center bg-navy-800 border border-navy-600 rounded-lg p-8">
          <p className="text-base font-semibold text-white mb-2">Samtale ikke funnet</p>
          <p className="text-sm text-slate-400 mb-5">{error || 'Ingen data tilgjengelig for denne samtalen.'}</p>
          <button onClick={() => navigate(-1)} className="text-sm text-accent-blue hover:underline">Tilbake</button>
        </div>
      </div>
    );
  }

  const _ca = call.call_analysis || {}; const a = { ..._ca, ...(_ca.custom_analysis_data || {}), lead_score: call.lead_score ?? _ca.lead_score, urgency_score: call.urgency_score ?? _ca.urgency_score, call_intent: call.call_intent ?? _ca.call_intent, call_summary: call.call_summary ?? _ca.call_summary, buying_signal: call.buying_signal ?? _ca.buying_signal, sentiment: call.sentiment ?? _ca.sentiment ?? _ca.user_sentiment, caller_name: call.caller_name ?? _ca.caller_name, appointment_booked: call.appointment_booked ?? _ca.appointment_booked, callback_requested: call.callback_requested ?? _ca.callback_requested, service_requested: call.service_requested ?? _ca.service_requested, action_items: call.action_items ?? _ca.action_items, is_existing_customer: call.is_existing_customer ?? _ca.is_existing_customer };
  const callDate = new Date(call.created_at).toLocaleString('nb-NO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  // Use shared formatDuration from utils

  return (
    <div data-testid="call-detail-page">
      {/* Header */}
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
      >
        <ArrowLeft size={16} /> Tilbake
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          {(() => {
            let isStarred = false;
            try {
              const raw = localStorage.getItem('starredCalls');
              const set = new Set(raw ? JSON.parse(raw) : []);
              isStarred = set.has(call.call_id);
            } catch {}
            return (
              <button
                type="button"
                onClick={() => {
                  try {
                    const raw = localStorage.getItem('starredCalls');
                    const arr = raw ? JSON.parse(raw) : [];
                    const set = new Set(arr);
                    if (set.has(call.call_id)) set.delete(call.call_id); else set.add(call.call_id);
                    localStorage.setItem('starredCalls', JSON.stringify(Array.from(set)));
                    // Force re-render by toggling a state hack
                    window.dispatchEvent(new Event('storage'));
                    setCall({ ...call }); // shallow clone triggers re-render
                  } catch {}
                }}
                aria-label={isStarred ? 'Fjern stjerne' : 'Stjernemerk'}
                title={isStarred ? 'Fjern stjernemerking' : 'Stjernemerk denne samtalen'}
                data-testid="call-detail-star"
                className={`p-2 rounded-lg border transition-colors mt-1 ${
                  isStarred
                    ? 'border-accent-yellow text-accent-yellow bg-accent-yellow/10'
                    : 'border-navy-600 text-slate-500 hover:text-accent-yellow hover:border-accent-yellow/40'
                }`}
              >
                <Star size={18} fill={isStarred ? 'currentColor' : 'none'} />
              </button>
            );
          })()}
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
            {a.caller_name || 'Ukjent innringer'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">{callDate}</p>
        </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
            a.sentiment === 'Positive' ? 'bg-accent-green/15 text-accent-green' :
            a.sentiment === 'Negative' ? 'bg-accent-red/15 text-accent-red' :
            'bg-slate-500/15 text-slate-400'
          }`}>{a.sentiment || 'Ukjent'}</span>
          {a.appointment_booked && (
            <span className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-400 font-medium">
              Avtale booket
            </span>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6">
        <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-3">Sammendrag</h3>
        <p className="text-sm text-slate-300 leading-relaxed">{a.call_summary || 'Ingen sammendrag tilgjengelig.'}</p>
      </div>

      {call.textback_sent && (
        <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-4 mb-6 flex items-center gap-3">
          <span className="text-lg">📲</span>
          <div>
            <span className="text-sm font-medium text-teal-400">Automatisk SMS sendt</span>
            <p className="text-xs text-slate-400 mt-0.5">En tekstmelding ble automatisk sendt til denne innringeren etter tapt anrop.</p>
          </div>
        </div>
      )}

      {/* Metadata grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetaBadge icon={Phone} label="Telefon" value={call.from_number || '-'} color="text-accent-blue" />
        <MetaBadge icon={Clock} label="Varighet" value={formatDuration(call.duration_seconds)} color="text-accent-yellow" />
        <MetaBadge icon={Flame} label="Lead Score" value={`${a.lead_score || 0}/100`} color="text-accent-green" />
        <MetaBadge icon={MessageSquare} label="Henvendelse" value={a.call_intent || '-'} color="text-purple-400" />
        <MetaBadge icon={User} label="Tjeneste" value={a.service_requested || '-'} color="text-accent-blue" />
        <MetaBadge icon={Calendar} label="Kjøpssignal" value={a.buying_signal || '-'} color="text-accent-yellow" />
        <MetaBadge icon={User} label="Eksisterende kunde" value={a.is_existing_customer ? 'Ja' : 'Nei'} color="text-slate-400" />
        <MetaBadge icon={Phone} label="Haster" value={`${a.urgency_score || 0}/10`} color="text-accent-red" />
      </div>

      {/* Audio - render whenever we have a call_id; playback-url endpoint resolves missing URLs */}
      {call.call_id && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-3">Opptak</h3>
          <AudioPlayer url={call.recording_url || ''} callId={call.call_id} />
          {/* resolveError is rendered inside AudioPlayer when url is empty */}
        </div>
      )}

      {/* Quality rating */}
      <QualityRatingCard call={call} onRated={(r) => setCall(prev => prev ? { ...prev, quality_rating: r.rating, quality_rating_comment: r.comment, quality_rating_at: r.rated_at } : prev)} />

      {/* Notes */}
      <NotesCard call={call} />

      {/* Transcript */}
      <TranscriptCard call={call} />

      {/* Follow-Up Suggestions */}
      {(() => {
        const isRecent = call.created_at && (Date.now() - new Date(call.created_at).getTime()) < 2 * 60 * 1000;
        const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
        if (pendingSuggestions.length === 0 && !isRecent) return null;
        return (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mt-6" data-testid="suggestions-section">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={14} className="text-accent-yellow" />
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500">{t('suggestions_title')}</h3>
            </div>
            {pendingSuggestions.length === 0 ? (
              <p className="text-sm text-slate-500 animate-pulse">{t('suggestions_analyzing')}</p>
            ) : (
              <div className="space-y-3">
                {pendingSuggestions.map(s => (
                  <div key={s._id} className="bg-navy-700 border border-navy-600 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                            s.priority === 'high' ? 'bg-accent-red/15 text-accent-red' :
                            s.priority === 'medium' ? 'bg-accent-yellow/15 text-accent-yellow' :
                            'bg-slate-500/15 text-slate-400'
                          }`}>{s.priority}</span>
                        </div>
                        <p className="text-sm text-white font-medium mt-1">{s.action}</p>
                        {s.reason && <p className="text-xs text-slate-400 mt-1">{s.reason}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleSuggestionAction(s._id, 'done')}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-accent-green/15 text-accent-green hover:bg-accent-green/25 transition-colors"
                        >
                          <CheckCircle size={12} /> {t('suggestions_markDone')}
                        </button>
                        <button
                          onClick={() => handleSuggestionAction(s._id, 'dismissed')}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-500/15 text-slate-400 hover:bg-slate-500/25 transition-colors"
                        >
                          <XCircle size={12} /> {t('suggestions_dismiss')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
